<script setup lang="ts">
import { DgrIcon, IconTypes } from '@stockmarkteam/donguri-ui';
import { useTooltipVisibility } from '@/utils/composables/useTooltipVisibility';

withDefaults(
  defineProps<{
    name: string;
    icon?: IconTypes;
    count?: number;
    isDisabled?: boolean;
    isExternalLink?: boolean;
    isSelected?: boolean;
    isDraggable?: boolean;
    rightIcon?: IconTypes;
  }>(),
  {
    isDisabled: false,
    isExternalLink: false,
    isSelected: false,
    isDraggable: false,
  },
);

const { elementRef: titleRef, isTooltipVisible } = useTooltipVisibility();
</script>

<template>
  <div
    class="item"
    :class="{
      selected: isSelected,
      disabled: isDisabled,
    }"
  >
    <span class="name-icon c-text-regular--m">
      <template v-if="icon">
        <DgrIcon
          size="small"
          :name="icon"
          :keep-fill="false"
          class="icon"
          :class="{ draggable: isDraggable }"
        />
        <DgrIcon
          size="small"
          name="bars"
          :keep-fill="false"
          v-if="isDraggable"
          class="icon drag-icon"
        />
      </template>
      <VTooltip
        placement="bottom"
        :disabled="!isTooltipVisible"
        :prevent-overflow="true"
        class="name"
      >
        <span ref="titleRef">{{ name }}</span>
        <template #popper>{{ name }}</template>
      </VTooltip>
      <DgrIcon size="small" name="external-link" v-if="isExternalLink" />
    </span>
    <span v-if="count && count > 0" class="count c-text-regular--xs">{{
      count
    }}</span>
    <DgrIcon
      v-if="rightIcon"
      size="small"
      :name="rightIcon"
      class="right-icon"
    />
  </div>
</template>

<style scoped lang="scss">
.item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;

  box-sizing: border-box;
  width: var(--menu-item-width, 240px);
  height: 40px;
  border-radius: 4px;
  padding: 8px;

  background: #fff;
  cursor: pointer;

  &:hover {
    background: $color-gray200;

    .name-icon {
      .icon.draggable {
        display: none;
      }
      .drag-icon {
        display: flex;
      }
    }
  }

  &.selected {
    color: $color-green600;
    background: $color-green100;

    &:hover {
      background: $color-green100;
    }
    .icon-box {
      color: $color-green600;
    }
    .count {
      color: $color-green600;
    }
  }

  &.disabled {
    cursor: pointer;
    pointer-events: none;
    color: $color-gray600;

    .icon-box {
      color: $color-gray600;
    }

    &:hover {
      background: #fff;
    }
  }

  .name-icon {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;

    overflow: hidden;

    .icon {
      flex-shrink: 0;
    }
    .icon.draggable,
    .drag-icon {
      cursor: move;
    }

    .drag-icon {
      display: none;
    }

    .name {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
  .count {
    flex-shrink: 0;
    color: $color-gray600;
  }
  .right-icon {
    flex-shrink: 0;
    color: var(--menu-item-right-icon-color, currentColor);
  }
}
</style>
